/* helper.ts */

/* Chroma */
import chroma from "chroma-js";

/* Types */
import { SVGSelection } from "src/shared/types/d3";

/* Utils */
import { getConsistentColor } from "src/utils/helper/charts/colorMapper";
import { transform } from "src/utils/helper/string-transform/StringTransformer";

/* Constants */
import { CustomFeature } from "src/shared/constants/common/CustomFeature";

/* Interfaces */
import { BoxplotProps } from "src/shared/interfaces/Boxplot";
import { HorizontalBoxplotTooltipProps } from "src/shared/interfaces/ChartTooltip";

export const createColors = (
  label: string,
  customFeature?: CustomFeature
) => {
  switch (customFeature) {
    case CustomFeature.TREATMENT: {
      return getConsistentColor(
        transform(label)
          .stripUnitSuffix()
          .stripLabelSuffix()
          .toString()
      );
    }
    case CustomFeature.MEDICATION: {
      return getConsistentColor(
        transform(label)
          .capitalizeFirstLetter()
          .stripUnitSuffix()
          .stripLabelSuffix()
          .toString()
      );
    }
    default: {
      return getConsistentColor(label);
    }
  }
};

export const createTransformedLabel = (
  label: string,
  customFeature?: CustomFeature
) => {
  switch (customFeature) {
    case CustomFeature.TREATMENT:
      return transform(label)
        .stripUnitSuffix()
        .stripLabelSuffix()
        .toString();
    case CustomFeature.MEDICATION:
      return transform(label)
        .capitalizeFirstLetter()
        .toString();
    default:
      return label;
  }
};

export const createGradient = (
  svg: SVGSelection,
  data: BoxplotProps[],
  gradientPrefix: string,
  customFeature?: CustomFeature
) => {
  const defs = svg.append("defs");

  data.map((d, i) => {
    const gradientId = `${gradientPrefix}-gradient-${i}`;

    if (
      !defs
        .select(`linearGradient[id="${gradientId}"]`)
        .empty()
    )
      return;

    const gradient = defs
      .append("linearGradient")
      .attr("id", gradientId)
      .attr("gradientUnits", "userSpaceOnUse")
      .attr("x1", "0%")
      .attr("x2", "0%")
      .attr("y1", "0%")
      .attr("y2", "100%");

    const baseColor = createColors(d.name, customFeature);

    gradient
      .append("stop")
      .attr("offset", "10%")
      .attr(
        "stop-color",
        chroma(baseColor).brighten(1).alpha(0.9).hex()
      );

    gradient
      .append("stop")
      .attr("offset", "50%")
      .attr("stop-color", chroma(baseColor).alpha(1).hex());

    gradient
      .append("stop")
      .attr("offset", "100%")
      .attr(
        "stop-color",
        chroma(baseColor).darken(1).alpha(1).hex()
      );
  });
};

/**
 * Set tooltip data for the horizontal boxplot.
 *
 * @param {HorizontalBoxplotTooltipProps} tooltipData
 * @returns {Array<{ key: string; value: string | undefined }> | undefined}
 */
export const setTooltipData = (
  tooltipData: HorizontalBoxplotTooltipProps
):
  | Array<{ key: string; value: string | undefined }>
  | undefined => {
  if (!tooltipData) return;

  const { min, q1, median, mean, max, outliers } =
    tooltipData;

  if (outliers) {
    return [{ key: "Value", value: outliers?.toFixed(2) }];
  } else {
    return [
      { key: "Min", value: min?.toFixed(2) },
      { key: "Q1", value: q1?.toFixed(2) },
      { key: "Median", value: median?.toFixed(2) },
      { key: "Mean", value: mean?.toFixed(2) },
      { key: "Q3", value: max?.toFixed(2) },
      { key: "Max", value: max?.toFixed(2) },
    ];
  }
};
